import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-recovery-code',
  templateUrl: './recovery-code.component.html',
  styleUrls: ['./recovery-code.component.scss'],
})
export class RecoveryCodeComponent implements OnInit, OnDestroy {
  @Input() email: string = '';
  // CPK-4585: when the client came in through the CPF recovery form instead
  // of the plain email one, resendCode/submit hit the CPF-based endpoints
  // instead of the Supabase email OTP.
  @Input() recoveryMode: 'email' | 'cpf' = 'email';
  @Input() cpf: string = '';
  @Input() phone: string = '';

  isLoading = false;
  errorMessage: string = '';
  captchaToken: string | null = null;

  lastSendTime: number | null = null;
  sendCodePermission = false;

  form: FormGroup<any> = new FormGroup({});
  @Output() onSubmit: EventEmitter<any> = new EventEmitter<any>();

  subscriptions: Subscription[] = [];
  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private actRoute: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      code: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    // Clear error message when user types in the code field
    this.subscriptions.push(
      this.form.get('code')?.valueChanges.subscribe(() => {
        if (this.errorMessage) {
          this.errorMessage = '';
        }
      }) || new Subscription()
    );

    setInterval(() => {
      const now = new Date().getTime();
      if (!this.lastSendTime) {
        this.sendCodePermission = true;
        return;
      }

      if (this.lastSendTime && now - this.lastSendTime > 1000 * 60) {
        this.sendCodePermission = true;
      } else {
        this.sendCodePermission = false;
      }
    }, 1000);

    this.subscriptions.push(
      this.actRoute.queryParams.subscribe((params) => {
        if (params['email']) this.email = params['email'];
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  getDiffLastTimeAndNowInSec() {
    if (!this.lastSendTime) return 0;
    return 60 - Math.floor((new Date().getTime() - this.lastSendTime) / 1000);
  }

  async resendCode() {
    try {
      this.isLoading = true;

      if (this.recoveryMode === 'cpf') {
        if (!this.captchaToken) throw Error('Captcha is required');
        await this.userService.requestAccountRecoveryByCpf(
          this.cpf,
          this.email,
          this.phone,
          this.captchaToken
        );
      } else {
        if (!this.email) throw Error('Email is required');
        const { error } = await this.userService.sendResetPasswordEmail(this.email);
        if (error) throw Error('Issue sending email');
      }

      this.lastSendTime = new Date().getTime();
      this.sendCodePermission = false;
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async submit() {
    try {
      this.isLoading = true;
      this.errorMessage = ''; // Clear previous errors

      const code = this.form.get('code')?.value;
      if (!code) throw Error('Code is required');

      if (this.recoveryMode === 'cpf') {
        const { user, session } = await this.userService.verifyAccountRecoveryOtp(
          this.cpf,
          this.email,
          this.phone,
          code
        );
        this.onSubmit.emit({ user, session });
        return;
      }

      if (!this.email) throw Error('Email is required');
      const { user, session } = await this.userService.verifyPasswordResetCodeChallenger(
        this.email,
        code
      );

      this.onSubmit.emit({ user, session });
    } catch (error: any) {
      console.error(error);

      // Check if it's an OTP expired/invalid error (403 status)
      if (error.message && (error.message.includes('otp_expired') ||
          error.message.includes('Token has expired or is invalid') ||
          error.message.includes('Invalid or expired') ||
          error.message.includes('Invalid'))) {
        // Store translation key for the error message
        this.errorMessage = 'Código inválido ou expirado';
      } else {
        // Generic error message
        this.errorMessage = 'Erro ao verificar código. Tente novamente.';
      }
    } finally {
      this.isLoading = false;
    }
  }
}
